import { useQuery } from '@tanstack/react-query';
import { fetchBaumFunktionen, type BaumFunktionen } from '@/api/trees.api';
import { useTreeContextStore } from '@/store/useTreeContextStore';

// ─── Was der Tarif in diesem Stammbaum freischaltet ─────────────────────────
//
// Maßgeblich ist der Tarif des BESITZERS. Für ein Mitglied heißt das: was hier
// geht, hängt nicht am eigenen Tarif, sondern daran, was der Besitzer gebucht
// hat. Deshalb hängt die Abfrage am Stammbaum und nicht am Konto.
//
// Solange noch geladen wird, gilt alles als freigeschaltet. Andersherum
// flackerten bei jedem Seitenwechsel Menüpunkte auf und wieder weg, und ein
// Direktaufruf landete kurz auf der Sperrseite, obwohl er erlaubt ist. Die
// Durchsetzung liegt ohnehin auf dem Server – hier geht es nur darum, nichts
// anzubieten, was ins Leere führt.

export interface Funktionsstand {
  /** Ist diese Funktion freigeschaltet? */
  hat: (feature: string) => boolean;
  tarif: string | null;
  geladen: boolean;
  daten: BaumFunktionen | undefined;
  /** Beim Server nachfragen, statt der zwischengespeicherten Liste zu glauben. */
  nachpruefen: () => Promise<unknown>;
}

export function useTreeFeatures(): Funktionsstand {
  const activeTreeId = useTreeContextStore((s) => s.activeTreeId);

  const { data, isSuccess, refetch } = useQuery({
    queryKey: ['trees', 'features', activeTreeId],
    queryFn: () => fetchBaumFunktionen(activeTreeId!),
    enabled: Boolean(activeTreeId),
    // Fünf Minuten, nicht eine Stunde.
    //
    // Der Tarif ändert sich selten – aber wenn, dann merkt es der Kunde
    // sofort: der Betreiber schaltet etwas frei, und die Anwendung behauptet
    // eine Stunde lang weiter, es sei nicht gebucht. Genau das ist passiert.
    //
    // Fünf Minuten sparen die Abfrage bei jedem Seitenwechsel immer noch,
    // begrenzen den Irrtum aber auf eine Spanne, in der niemand zum Hörer
    // greift.
    staleTime: 5 * 60 * 1000,
    // Wer im Nebenfenster den Tarif ändert und zurückwechselt, bekommt beim
    // Zurückkommen den neuen Stand. Das ist der Ablauf, in dem der Fehler
    // aufgefallen ist.
    refetchOnWindowFocus: true,
  });

  return {
    hat: (feature: string) => (isSuccess && data ? data.features.includes(feature) : true),
    tarif: data?.tarif ?? null,
    geladen: isSuccess,
    daten: data,
    nachpruefen: refetch,
  };
}
